import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MetricCard } from "@/components/admin/MetricCard";
import { ErrorDetailDrawer } from "@/components/admin/ErrorDetailDrawer";
import { getSeverityBadgeClass } from "@/components/admin/SeverityToggle";
import {
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  Bug,
  BarChart3,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import type {
  UnifiedError,
  ErrorSummary,
  ErrorDistribution,
  ErrorSource,
  ErrorFilters,
} from "@/lib/error-types";

const ERROR_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#22c55e", // green
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#0ea5e9", // sky
  "#3b82f6", // blue
];

interface ErrorTabContentProps {
  source: ErrorSource;
  errors: UnifiedError[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  summary: ErrorSummary | null;
  distribution: ErrorDistribution[];
  summaryLoading: boolean;
  distributionLoading: boolean;
  selectedErrorCode: string | null;
  onSelectErrorCode: (code: string | null) => void;
  onPageChange: (page: number) => void;
}

export function ErrorTabContent({
  source,
  errors,
  total,
  page,
  pageSize,
  loading,
  summary,
  distribution,
  summaryLoading,
  distributionLoading,
  selectedErrorCode,
  onSelectErrorCode,
  onPageChange,
}: ErrorTabContentProps) {
  const [selectedError, setSelectedError] = useState<UnifiedError | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [chartOpen, setChartOpen] = useState(true);

  const totalPages = Math.ceil(total / pageSize);

  // Row selection handlers
  const toggleRowSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === errors.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(errors.map((e) => e.id)));
    }
  };

  const copySelectedRows = async () => {
    const selectedErrors = errors.filter((e) => selectedRows.has(e.id));
    const text = JSON.stringify(selectedErrors, null, 2);
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`Copied ${selectedErrors.length} error(s) to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBarClick = (data: { code: string }) => {
    if (selectedErrorCode === data.code) {
      onSelectErrorCode(null);
    } else {
      onSelectErrorCode(data.code);
    }
  };

  const handleRowClick = (error: UnifiedError) => {
    setSelectedError(error);
    setDrawerOpen(true);
  };

  const handlePrevPage = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  };

  // Get label for most common based on source
  const getMostCommonLabel = () => {
    switch (source) {
      case "function":
        return "Most Failing";
      default:
        return "Most Common";
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Errors (24h)"
          value={summary?.total_24h ?? 0}
          icon={AlertTriangle}
          loading={summaryLoading}
          variant={
            (summary?.total_24h ?? 0) > 10
              ? "error"
              : (summary?.total_24h ?? 0) > 0
              ? "warning"
              : "default"
          }
        />
        <MetricCard
          title="Errors (7d)"
          value={summary?.total_7d ?? 0}
          icon={Bug}
          loading={summaryLoading}
        />
        <MetricCard
          title="Error Rate (24h)"
          value={
            summary?.error_rate_24h
              ? `${summary.error_rate_24h.toFixed(1)}%`
              : "—"
          }
          icon={TrendingUp}
          loading={summaryLoading}
        />
        <MetricCard
          title={getMostCommonLabel()}
          value={summary?.most_common_code ?? "—"}
          icon={BarChart3}
          loading={summaryLoading}
          description={
            summary?.most_common_count
              ? `${summary.most_common_count} occurrences`
              : undefined
          }
        />
      </div>

      {/* Error Distribution Chart - Collapsible */}
      <Collapsible open={chartOpen} onOpenChange={setChartOpen}>
        <Card>
          <CardHeader className="pb-2">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Error Distribution
                </CardTitle>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform",
                  chartOpen && "rotate-180"
                )} />
              </Button>
            </CollapsibleTrigger>
            <CardDescription>
              Click a bar to filter the error log below
            </CardDescription>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {distributionLoading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : distribution.length === 0 ? (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  No errors in selected period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={distribution.slice(0, 10)}
                    layout="vertical"
                    margin={{ left: 80, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="code"
                      tick={{ fontSize: 10 }}
                      width={75}
                      tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                    />
                    <Tooltip
                      formatter={(value: number) => [value, "Count"]}
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "6px",
                      }}
                    />
                    <Bar
                      dataKey="count"
                      cursor="pointer"
                      onClick={(data) => handleBarClick(data)}
                    >
                      {distribution.slice(0, 10).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            selectedErrorCode === entry.code
                              ? "#3b82f6"
                              : ERROR_COLORS[index % ERROR_COLORS.length]
                          }
                          opacity={
                            selectedErrorCode && selectedErrorCode !== entry.code
                              ? 0.4
                              : 1
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Selected Filter Badge */}
      {selectedErrorCode && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filtered by:</span>
          <Badge
            variant="secondary"
            className="cursor-pointer"
            onClick={() => onSelectErrorCode(null)}
          >
            {selectedErrorCode} ×
          </Badge>
        </div>
      )}

      {/* Error Log Table */}
      <Card className="flex flex-col overflow-hidden">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Error Log
              <Badge variant="secondary" className="ml-2">
                {total}
              </Badge>
            </CardTitle>
            {selectedRows.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={copySelectedRows}
              >
                {copied ? (
                  <Check className="h-4 w-4 mr-2" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                <span className="hidden sm:inline">Copy</span> {selectedRows.size}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 flex-1 overflow-hidden">
          <ScrollArea className="h-[400px]">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-10 px-2">
                      <Checkbox
                        checked={errors.length > 0 && selectedRows.size === errors.length}
                        onCheckedChange={toggleAllRows}
                        aria-label="Select all"
                        disabled={loading || errors.length === 0}
                      />
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Time</TableHead>
                    <TableHead className="hidden sm:table-cell">Severity</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="w-full">Message</TableHead>
                    <TableHead className="hidden md:table-cell whitespace-nowrap">User</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="px-2">
                          <Skeleton className="h-4 w-4" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Skeleton className="h-6 w-14" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-full max-w-[200px]" />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : errors.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No errors found
                      </TableCell>
                    </TableRow>
                  ) : (
                    errors.map((error) => (
                      <TableRow
                        key={error.id}
                        className={cn(
                          "cursor-pointer hover:bg-muted/50",
                          selectedRows.has(error.id) && "bg-muted/30"
                        )}
                        onClick={() => handleRowClick(error)}
                      >
                        <TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedRows.has(error.id)}
                            onCheckedChange={() => {
                              setSelectedRows((prev) => {
                                const next = new Set(prev);
                                if (next.has(error.id)) {
                                  next.delete(error.id);
                                } else {
                                  next.add(error.id);
                                }
                                return next;
                              });
                            }}
                            aria-label={`Select error ${error.code}`}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3 hidden sm:inline" />
                            {format(new Date(error.created_at), "MMM d, HH:mm")}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge
                            className={cn(
                              "text-xs",
                              getSeverityBadgeClass(error.severity)
                            )}
                          >
                            {error.severity}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {error.code.length > 16 ? error.code.slice(0, 16) + '...' : error.code}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="line-clamp-1" title={error.message || undefined}>
                            {error.message || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground whitespace-nowrap">
                          {error.user_email ||
                            (error.user_id
                              ? error.user_id.slice(0, 8) + "..."
                              : "—")}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          {/* Pagination */}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t flex-wrap gap-2">
              <div className="text-xs sm:text-sm text-muted-foreground">
                {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                  {page}/{totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={page >= totalPages}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Detail Drawer */}
      <ErrorDetailDrawer
        error={selectedError}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}
